import React, { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import { LuFileSearch } from 'react-icons/lu';
import { confirm } from 'components/common';
import { escapeHtml } from 'helpers/utils';
import {
  createDraftComparison, deleteDraftComparison, getComparison, listDraftComparisons,
} from 'components/app/document-compare/documentCompareApi';
import { errorOf } from 'components/app/document-compare/compareFormat';
import ComparisonResult, { Notice } from 'components/app/document-compare/ComparisonResult';
import useComparisonPolling from 'components/app/document-compare/useComparisonPolling';
import DraftUploadForm from 'components/app/document-compare/draft/DraftUploadForm';
import DraftHistory from 'components/app/document-compare/draft/DraftHistory';

// Commercial → Draft Check: check a draft Contract / Proposal Sales is still
// negotiating — not yet a file of any Contract record — against PEB's standard, and
// download it back with every difference highlighted and commented.
const DraftCheckPage = () => {
  const [history, setHistory] = useState(null);
  const [current, setCurrent] = useState(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const busyRef = useRef(false);
  const [formKey, setFormKey] = useState(0);

  useEffect(() => {
    listDraftComparisons()
      .then((res) => setHistory(res.data))
      .catch((e) => { setHistory([]); setError(errorOf(e, 'Could not load your drafts.')); });
  }, []);

  // Keep the list row in step with the comparison being followed.
  const update = useCallback((data) => {
    setCurrent(data);
    setHistory((old) => (old || []).map((c) => (c.id === data.id ? { ...c, ...data } : c)));
  }, []);
  const { resetFailures } = useComparisonPolling({ active: true, current, onUpdate: update, onError: setError });

  const start = async ({ standardFile, file }) => {
    if (busyRef.current) return;
    busyRef.current = true; setBusy(true); setError('');
    try {
      const res = await createDraftComparison({ standardFile, file });
      setCurrent(res.data);
      setHistory((old) => [res.data, ...(old || [])]);
      setFormKey((k) => k + 1);
    } catch (e) {
      setError(errorOf(e, 'Could not start the comparison.'));
    } finally {
      busyRef.current = false; setBusy(false);
    }
  };

  const open = async (id) => {
    setError('');
    resetFailures();
    try { setCurrent((await getComparison(id)).data); } catch (e) { setError(errorOf(e, 'Could not open that comparison.')); }
  };

  const remove = async (comparison) => {
    if (deletingId) return;
    const ok = await confirm({
      title: 'Delete this draft?',
      message: `<strong>${escapeHtml(comparison.sales_filename)}</strong> and its marked-up copy will be deleted.`,
      confirmText: 'Delete',
    });
    if (!ok) return;
    setDeletingId(comparison.id);
    try {
      await deleteDraftComparison(comparison.id);
      setHistory((old) => old.filter((c) => c.id !== comparison.id));
      if (current?.id === comparison.id) setCurrent(null);
    } catch (e) {
      toast.error(errorOf(e, 'Could not delete the draft.'));
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <div className="tw-flex tw-flex-col tw-gap-3 tw-p-4">
      <div className="tw-flex tw-items-center tw-gap-3">
        <div className="tw-w-1.5 tw-h-10 tw-rounded-sm tw-flex-shrink-0" style={{ background: 'linear-gradient(to bottom, #0ea5e9, #6366f1)' }} />
        <LuFileSearch className="tw-text-blue-600" size={22} aria-hidden="true" />
        <div>
          <h1 className="tw-mb-0 tw-font-bold tw-text-2xl tw-text-gray-900">Draft Check</h1>
          <p className="tw-text-gray-500 tw-text-sm tw-mb-0">
            Upload the standard and the draft: every difference is highlighted in the draft. Only you see the files you upload.
          </p>
        </div>
      </div>

      <div className="tw-grid tw-grid-cols-1 lg:tw-grid-cols-[340px_minmax(0,1fr)] tw-gap-3 tw-items-start">
        <aside className="tw-flex tw-flex-col tw-gap-3">
          <div className="tw-bg-white tw-rounded-xl tw-border tw-border-solid tw-border-slate-200 tw-p-4">
            <DraftUploadForm key={formKey} busy={busy} onSubmit={start} />
          </div>
          <div className="tw-bg-white tw-rounded-xl tw-border tw-border-solid tw-border-slate-200 tw-p-3">
            <h2 className="tw-m-0 tw-mb-2 tw-px-2 tw-text-[13px] tw-font-bold tw-text-slate-700">My drafts</h2>
            <DraftHistory items={history} selectedId={current?.id} onOpen={open} onDelete={remove} deletingId={deletingId} />
          </div>
        </aside>

        <main className="tw-bg-white tw-rounded-xl tw-border tw-border-solid tw-border-slate-200 tw-p-4 tw-min-h-[240px] tw-flex tw-flex-col tw-gap-3">
          {error && <Notice tone="red">{error}</Notice>}
          {current ? (
            <>
              <h2 className="tw-m-0 tw-text-base tw-font-bold tw-text-slate-800 tw-break-all">{current.sales_filename}</h2>
              <ComparisonResult comparison={current} />
            </>
          ) : (
            <p className="tw-m-auto tw-text-[13px] tw-text-slate-500 tw-text-center">
              Upload the standard and a draft, or open one from My drafts, to see what differs.
            </p>
          )}
        </main>
      </div>
    </div>
  );
};

export default DraftCheckPage;
